import { ReactNode, useEffect, useRef } from 'react';
import {
  MessageInput,
  MessageInputProps,
  MessageList,
  MessageListProps,
  useChannelActionContext,
  useChannelStateContext,
  useMessageComposer,
} from 'stream-chat-react';
import { z } from 'zod';

import { useAssistantContext } from 'src/context';
import { ChannelStatusIndicators } from 'src/features/channel/channel-content/ChannelStatusIndicators';
import { usePrefill } from 'src/hooks/usePrefill';
// TODO in POR-15598: move MessageContent out of the legacy directory
// eslint-disable-next-line no-restricted-imports
import { MessageContent } from 'src/legacy/components/Chat/MessageContent';
import { AssistantUserID } from 'src/store/clients/types';
import { useCopyHandler } from 'src/utils/ClipboardUtils';

export {
  MAX_CHARACTERS_LIMIT,
  MAX_CHARACTERS_LIMIT_ERROR,
} from 'src/constants/messageConsts';

interface MessageListComponentProps extends MessageListProps {
  suppressAutoScroll?: boolean;
}
const MessageListComponent = (props: MessageListComponentProps) => {
  return <MessageList {...props} />;
};

const MessageInputComponent = (props: MessageInputProps) => {
  return (
    <div className="z-10">
      <MessageInput {...props} />
    </div>
  );
};

const prefillDataSchema = z
  .object({
    body: z.string(),
  })
  .passthrough();

const usePrefilledMessageDraft = ({
  isInternalChat,
}: {
  isInternalChat: boolean;
}) => {
  const { setTextComposer, setMessageDraft, isSetMessageDraftReady } =
    useAssistantContext();
  const { prefilledData, action } = usePrefill(
    prefillDataSchema,
    'sendMessage',
  );
  const prefillAppliedRef = useRef(false);
  const { textComposer } = useMessageComposer();

  // Register the message input with AssistantContext for prefill support.
  // TipTap editor is registered by CustomMessageInput; ChannelContent only needs
  // to expose the text composer for prefill support.
  useEffect(() => {
    if (!isInternalChat) {
      setTextComposer(textComposer);
    }
    return () => {
      setTextComposer(null);
    };
  }, [setTextComposer, textComposer, isInternalChat]);

  // Reset prefill flag when prefilled data changes
  useEffect(() => {
    prefillAppliedRef.current = false;
  }, [prefilledData?.body]);

  // Handle prefill using setMessageDraft from assistantContext
  useEffect(() => {
    if (
      !isInternalChat &&
      isSetMessageDraftReady &&
      prefilledData?.body &&
      !prefillAppliedRef.current &&
      action === 'sendMessage'
    ) {
      prefillAppliedRef.current = true;
      setMessageDraft(prefilledData.body);
    }
  }, [
    isInternalChat,
    prefilledData?.body,
    setMessageDraft,
    isSetMessageDraftReady,
    action,
  ]);
};

export const ChannelContent = ({
  ChannelToolbar,
  MessageListOverride,
  autoFocus = true,
  defaultInputValue = '',
  onChange,
  onSubmit,
  showMessageInput = true,
  placeholderText,
  messageId,
  aiAssistantStatus,
  typingStatus,
  isInternalChat = false,
  setAIAssistantStatus,
  clearAIAssistantStatus,
  cancelAIStream,
  getCompanyFromId,
  getClientFromId,
  allowScroll = false,
}: {
  ChannelToolbar?: ReactNode;
  MessageListOverride?: ReactNode;
  autoFocus?: boolean;
  defaultInputValue?: string;
  onChange?: (text: string) => void;
  onSubmit?: () => void;
  showMessageInput?: boolean;
  placeholderText?: string;
  messageId?: string;
  aiAssistantStatus?: {
    status: string;
    metadata?: {
      tool?: string;
      query?: string;
    };
    messageId?: string;
    isCancelable: boolean;
  } | null;
  typingStatus?: {
    typingUsers: Array<{
      user?: {
        id?: string;
        name?: string;
      };
    }>;
  } | null;
  isInternalChat?: boolean;
  setAIAssistantStatus?: (
    channelId: string,
    status: string,
    metadata?: { tool?: string; query?: string },
    messageId?: string,
  ) => void;
  clearAIAssistantStatus?: () => void;
  cancelAIStream?: () => void;
  getCompanyFromId?: (companyId: string) => { name?: string } | null;
  getClientFromId?: (clientId: string) => { name?: string } | null;
  allowScroll?: boolean;
}) => {
  const isFirstRender = useRef(true);

  const messageComposer = useMessageComposer();
  const { channel } = useChannelStateContext();

  usePrefilledMessageDraft({ isInternalChat });

  const { sendMessage, jumpToMessage } = useChannelActionContext();
  useEffect(() => {
    if (messageId && jumpToMessage) {
      void jumpToMessage(messageId);
    }
  }, [messageId, jumpToMessage]);

  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  // register onChange handler for the message composer
  useEffect(() => {
    if (onChange) {
      messageComposer.textComposer.middlewareExecutor.use([
        {
          id: 'input-state-handler',
          handlers: {
            onChange: async ({ state, next }) => {
              onChange?.(state.text);
              return next(state);
            },
            // we don't need to handle the suggestion item select
            // but since this is a required prop we need to define it
            // in the middleware executor
            onSuggestionItemSelect: async ({ state, next }) => {
              return next(state);
            },
          },
        },
      ]);
    }

    return () => {
      messageComposer.textComposer.middlewareExecutor.remove(
        'input-state-handler',
      );
    };
  }, [onChange, messageComposer.textComposer.middlewareExecutor]);

  const defaultInputValueRef = useRef(defaultInputValue);
  // Update textarea value when inputValue changes
  useEffect(() => {
    if (defaultInputValueRef.current && messageComposer.textComposer) {
      // Set the value and trigger both input and change events
      messageComposer.textComposer.setText(defaultInputValueRef.current);
    }
  }, [messageComposer.textComposer]);

  const handleCopyFromMessageList = useCopyHandler({
    skipTargetElements: ['textarea', 'input'],
  });

  return (
    <>
      {ChannelToolbar}
      <div
        onCopy={handleCopyFromMessageList}
        className={`flex-1 ${allowScroll ? 'overflow-x-hidden' : 'overflow-hidden'}`}
      >
        {MessageListOverride ? (
          MessageListOverride
        ) : (
          <MessageListComponent
            Message={MessageContent}
            suppressAutoScroll={false}
            scrolledUpThreshold={50}
          />
        )}
      </div>
      {/* Unified status indicators: AI assistant status and typing indicators */}
      <ChannelStatusIndicators
        aiAssistantStatus={aiAssistantStatus}
        typingStatus={typingStatus}
        isInternalChat={isInternalChat}
        getCompanyFromId={getCompanyFromId}
        getClientFromId={getClientFromId}
        clearAIAssistantStatus={clearAIAssistantStatus}
        cancelAIStream={cancelAIStream}
      />
      {showMessageInput && (
        <>
          <MessageInputComponent
            maxRows={10}
            additionalTextareaProps={{
              autoFocus: isFirstRender.current && autoFocus,
              placeholder: placeholderText,
            }}
            overrideSubmitHandler={async (params) => {
              if (onSubmit) {
                onSubmit();
              }

              // Set optimistic "sending" status for AI assistant only when AI is mentioned
              const channelId = channel?.cid;
              const isAIMentioned =
                params.localMessage?.mentioned_users?.some(
                  (user) => user.id === AssistantUserID,
                ) || false;

              try {
                if (isAIMentioned) {
                  const message = {
                    ...params.message,
                    user_timezone:
                      Intl.DateTimeFormat().resolvedOptions().timeZone,
                  };
                  params.message = message;
                }

                await sendMessage(params);

                if (
                  channelId &&
                  isInternalChat &&
                  isAIMentioned &&
                  setAIAssistantStatus
                ) {
                  // Set optimistic status without message ID - backend will "claim" it later
                  setAIAssistantStatus(channelId, 'sending', undefined);
                }
              } catch (error) {
                if (isInternalChat && isAIMentioned && clearAIAssistantStatus) {
                  clearAIAssistantStatus();
                }
              }
            }}
          />
        </>
      )}
    </>
  );
};
